// One account — the business customer record.
//
// Two halves, deliberately: the BILLING identity (terms, ABN, addressee, logo)
// because that is what an invoice needs, and the PEOPLE, because that is how you
// reach the business. `domain` is not shown: the owner does not want it and only
// 1 of 25 accounts has one.
import { Link, useParams } from "react-router-dom";
import { Page, Panel } from "../shell/Page";
import { useAccount, useAccountContacts } from "../hooks/useReads";
import { Avatar, Kv, Pill } from "../components/job-bits";
import { BackToTop } from "../components/back-to-top";
import { day } from "../lib/format";
import { lifecycleTone } from "./Contacts";
import { termsLabel } from "../lib/accounts";
import { NewOpportunityButton } from "../components/new-opportunity-dialog";

type Row = Record<string, unknown>;
const s = (v: unknown): string => (typeof v === "string" ? v : "");
const missing = <span className="text-muted-foreground">not recorded</span>;

export function Account() {
  const { id = "" } = useParams();
  const account = useAccount(id);
  const people = useAccountContacts(id);
  const a = (account.data ?? null) as Row | null;
  const rows = (people.data ?? []) as Row[];

  if (account.isPending) return <Page title="Account"><p className="text-sm text-muted-foreground">Loading…</p></Page>;
  if (!a) return <Page title="Account"><p className="text-sm text-muted-foreground">No such account.</p></Page>;

  return (
    <div className="page py-5">
      <div className="mb-3 flex items-center gap-2 text-micro text-muted-foreground">
        <Link className="text-primary" to="/accounts">Accounts</Link><span>/</span><span>{s(a.name)}</span>
      </div>

      <header className="mb-4">
        <h1 className="font-display text-2xl font-semibold tracking-tight">{s(a.name) || "Unnamed"}</h1>
        <p className="mt-0.5 text-sm text-muted-foreground">
          {s(a.category) || "no category"}{s(a.short_name) ? ` · ${s(a.short_name)}` : ""}
        </p>
      </header>

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Identity" meta="who they are">
          <Kv rows={[
            ["Name", s(a.name) || missing],
            ["Short name", s(a.short_name) || missing],
            ["Category", s(a.category) || missing],
            ["ABN", s(a.abn) || missing],
            ["Address", s(a.address) || missing],
            ["On file since", a.created_at ? day(String(a.created_at)) : missing],
          ]} />
        </Panel>

        <Panel title="Billing" meta="what an invoice needs">
          <Kv rows={[
            ["Terms", termsLabel(a.ar_days_expected)],
            ["PDF addressee", a.pdf_default_addressee_idx === null || a.pdf_default_addressee_idx === undefined
              ? missing
              : <span className="num">position {String(a.pdf_default_addressee_idx)}</span>],
            ["Logo", s(a.logo_url) ? "on file" : missing],
          ]} />
          <p className="mt-3 text-micro text-muted-foreground">
            A job under this business invoices the business. Use the personal-job switch on a job when it should bill the person instead.
          </p>
        </Panel>

        <Panel title={`People — ${rows.length}`} meta="who to call">
          {people.isPending && <p className="text-sm text-muted-foreground">Loading…</p>}
          {!people.isPending && rows.length === 0 && (
            <p className="text-sm text-muted-foreground">Nobody is attached to this business yet. Attach someone from their contact page.</p>
          )}
          <ul className="divide-y">
            {rows.map((c) => (
              <li key={s(c.id)} className="flex items-center gap-3 py-2">
                <Avatar name={s(c.name)} />
                <div className="min-w-0 grow">
                  <Link to={`/contacts/${s(c.id)}`} className="font-medium hover:underline">{s(c.name) || "Unnamed"}</Link>
                  <div className="truncate text-micro text-muted-foreground">{s(c.phone) || "no phone"}{s(c.email) ? ` · ${s(c.email)}` : ""}</div>
                </div>
                <NewOpportunityButton compact contactId={s(c.id)} name={s(c.name) || s(a.name)} />
                <Pill tone={lifecycleTone(s(c.lifecycle))}>{s(c.lifecycle) || "unknown"}</Pill>
              </li>
            ))}
          </ul>
        </Panel>
      </div>
      <BackToTop />
    </div>
  );
}
